import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import { Wallet } from "lucide-react";
import z from "zod";
import { OrderStatusBanner } from "@/components/order-status-banner";
import { kioskOrderQueryOptions } from "@/lib/kiosk-order";
import { createClient } from "@/lib/supabase/client";
import { formatNumberToCurrency } from "@/lib/utils";
import { websiteConfigQueryOptions } from "@/lib/website-config";

const kioskOrderSearchSchema = z.object({
  secret: z.string().optional(),
});

export const Route = createFileRoute("/$locale/kiosk/$orderId")({
  validateSearch: (search) => kioskOrderSearchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: async ({ params, search, context }) => {
    context.queryClient.ensureQueryData(websiteConfigQueryOptions());
    context.queryClient.ensureQueryData(
      kioskOrderQueryOptions(params.orderId, search.secret),
    );
  },
});

function RouteComponent() {
  const { orderId } = Route.useParams();
  const { secret } = Route.useSearch();

  //queries
  const { data: order } = useSuspenseQuery(
    kioskOrderQueryOptions(orderId, secret),
  );
  const { data: websiteConfig } = useSuspenseQuery(websiteConfigQueryOptions());

  //hooks
  const supabase = createClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("translation");

  const channelKey = `${order.reference}-${order.secret}`;

  // Same channel the POS already publishes every online-order status change
  // on, so moving a kiosk order to "ready" on the take-away screen shows up
  // here without the page asking.
  useEffect(() => {
    const orderChannel = supabase.channel(`order-update-channel-${channelKey}`);
    orderChannel.on<{ orderStatus: string; confirmedTime?: string | null }>(
      "broadcast",
      { event: "order-update" },
      (payload) => {
        queryClient.setQueryData(
          kioskOrderQueryOptions(orderId, secret).queryKey,
          (current) =>
            current && {
              ...current,
              status: payload.payload.orderStatus,
              confirmedTime: payload.payload.confirmedTime
                ? parseISO(payload.payload.confirmedTime)
                : current.confirmedTime,
            },
        );
      },
    );
    orderChannel.subscribe();
    return () => {
      void orderChannel.unsubscribe();
    };
  }, [channelKey, orderId, secret]);

  // An order that is still waiting for money is not being prepared, whatever
  // the take-away screen calls it. Say what the customer has to do instead of
  // "we are preparing your order".
  const awaitingPayment =
    !order.isPaid &&
    order.status !== "canceled" &&
    order.status !== "delivered";

  // A dine-in kiosk order is picked up at the counter like a pickup order, so
  // the banner copy for pickup fits it.
  const bannerOrderType =
    order.orderType === "delivery" ? "delivery" : "pickup";

  return (
    <div className="px-3 md:px-4 py-6 md:py-10 flex justify-center">
      <div className="w-full max-w-2xl space-y-4">
        <section className="rounded-2xl border border-border bg-card p-6 md:p-8 text-center shadow-sm">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            {t("KioskOrder.number_label")}
          </p>
          <p
            className="mt-2 text-6xl md:text-7xl font-bold tabular-nums leading-none"
            style={{ color: websiteConfig.primaryColor ?? undefined }}
          >
            {order.reference}
          </p>
          <p className="mt-4 text-muted-foreground">
            {t("KioskOrder.number_hint")}
          </p>
        </section>

        {awaitingPayment ? (
          <section className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 md:p-8 text-center shadow-lg">
            <div className="flex flex-col items-center gap-4">
              <div className="inline-flex h-12 w-12 md:h-16 md:w-16 items-center justify-center rounded-full border-2 border-amber-200 bg-amber-100 shadow-md">
                <Wallet className="h-6 w-6 md:h-8 md:w-8 text-amber-700" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-amber-900">
                {t("KioskOrder.unpaid_title")}
              </h2>
              <p className="text-base md:text-lg leading-relaxed max-w-md text-amber-800">
                {t("KioskOrder.unpaid_message")}
              </p>
            </div>
          </section>
        ) : (
          <OrderStatusBanner
            status={order.status}
            orderType={bannerOrderType}
            confirmedTime={order.confirmedTime}
          />
        )}

        {order.summary.items.length > 0 && (
          <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <h3 className="text-lg font-semibold mb-4">
              {t("Checkout.order_summary")}
            </h3>
            <ul className="space-y-2 mb-4">
              {order.summary.items.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <span className="text-foreground">
                    {item.name}
                    <span className="text-muted-foreground ml-1">
                      ×{item.quantity}
                    </span>
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatNumberToCurrency({
                      number: item.price * item.quantity,
                    })}
                  </span>
                </li>
              ))}
            </ul>
            <div className="space-y-2 border-t border-border pt-3">
              {order.summary.fees?.map((fee, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <span className="text-muted-foreground">{fee.label}</span>
                  <span className="tabular-nums">
                    {formatNumberToCurrency({ number: fee.amount })}
                  </span>
                </div>
              ))}
              {order.summary.discount && order.summary.discount.amount > 0 && (
                <div className="flex items-center justify-between gap-4 text-sm text-green-600">
                  <span>{order.summary.discount.label}</span>
                  <span className="tabular-nums font-medium">
                    -
                    {formatNumberToCurrency({
                      number: order.summary.discount.amount,
                    })}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 pt-2 font-semibold">
                <span>{t("Checkout.OrderSummary.total")}</span>
                <span className="tabular-nums">
                  {formatNumberToCurrency({ number: order.summary.total })}
                </span>
              </div>
              <p className="pt-1 text-right text-xs text-muted-foreground">
                {order.isPaid
                  ? t("KioskOrder.paid")
                  : t("KioskOrder.not_paid_yet")}
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
